import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import ReportHeader from './ReportHeader';
import { Button } from 'react-bootstrap';

const ReceiptDonationsSummaryDashboard = () => {
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timePeriod, setTimePeriod] = useState('currentYear');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState('');
  const [quarter, setQuarter] = useState('');

  const fetchSummaryData = useCallback(async () => {
    try {
      setLoading(true);

      if (timePeriod === 'custom' && year < 1992) {
        throw new Error('Year cannot be before 1992.');
      }

      const queryParams = new URLSearchParams();
      queryParams.append('timePeriod', timePeriod);
      queryParams.append('year', year);

      if (timePeriod === 'custom') {
        if (month) {
          queryParams.append('month', month);
        } else if (quarter) {
          queryParams.append('quarter', quarter);
        }
      }

      console.log(`Fetching data with params: ${queryParams.toString()}`);
      const response = await api.get(`/api/receiptsummary?${queryParams.toString()}`);
      setSummaryData(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [timePeriod, year, month, quarter]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  const resetYearMonthQuarter = useCallback(() => {
    setYear(new Date().getFullYear());
    setMonth('');
    setQuarter('');
  }, []);

  const handleTimePeriodChange = (e) => {
    setTimePeriod(e.target.value);
    resetYearMonthQuarter();
  };

  const years = Array.from({ length: new Date().getFullYear() - 1991 }, (_, i) => 1992 + i);

  // Define constants for current and previous year
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const quarters = [
    { value: '1', label: 'Q1 (Jan - Mar)' },
    { value: '2', label: 'Q2 (Apr - Jun)' },
    { value: '3', label: 'Q3 (Jul - Sep)' },
    { value: '4', label: 'Q4 (Oct - Dec)' }
  ];

  const getCardTitle = () => {
    switch (timePeriod) {
      case 'currentYear':
        return `Current Year (YTD) ${currentYear} Summary`;
      case 'lastYear':
        return `Last Year ${lastYear} Summary`;
      case 'custom':
        if (month) {
          return `Summary for ${months[month - 1]} ${year}`;
        } else if (quarter) {
          return `Summary for ${quarters.find(q => q.value === quarter).label} ${year}`;
        } else {
          return `Summary for ${year}`;
        }
      default:
        return 'Summary';
    }
  };

  const summaryRightValues = [
    'Donations Summary',
    `${getCardTitle()}`,
    `Report Date: ${new Intl.DateTimeFormat('en-US').format(new Date())}`
  ];

  const handlePrintAll = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error.message === 'Year cannot be before 1992.'
          ? error.message
          : 'Error fetching summary data. Please try again later.'}
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h2 className="mb-4 no-print">Donations Summary All Donors → Overall Donations Summary</h2>
      <div className="mb-4 no-print">
        <select
          className="form-select no-print"
          value={timePeriod}
          onChange={handleTimePeriodChange}
        >
          <option value="currentYear">Current Year ({currentYear})</option>
          <option value="lastYear">Last Year ({lastYear})</option>
          <option value="custom">Custom</option>
        </select>
      </div>

      {timePeriod === 'custom' && (
        <div className="mb-4 no-print">
          <label className="form-label" htmlFor="year-select">Year</label>
          <select
            id="year-select"
            className="form-select"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>

          <label className="form-label mt-2" htmlFor="quarter-select">Quarter (Optional)</label>
          <select
            id="quarter-select"
            className="form-select"
            value={quarter}
            onChange={(e) => {
              setQuarter(e.target.value);
              setMonth(''); // Reset month when a quarter is selected
            }}
          >
            <option value="">Select Quarter</option>
            {quarters.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
          </select>

          <label className="form-label mt-2" htmlFor="month-select">Month (Optional)</label>
          <select
            id="month-select"
            className="form-select"
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              setQuarter(''); // Reset quarter when a month is selected
            }}
          >
            <option value="">Full Year</option>
            {months.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
        </div>
      )}

      {summaryData.length > 0 && (
        <div>
          <div className="d-flex justify-content-end">
            <Button className="no-print small-font" onClick={handlePrintAll}>
              Print
            </Button>
          </div>
          <ReportHeader summaryRight={summaryRightValues} />
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">{getCardTitle()}</h5>

              {/* Table structure for Month, Amount, Unique Donors */}
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th scope="col">Month</th>
                    <th scope="col" style={{ textAlign: 'right' }}>Amount</th>
                    <th scope="col" style={{ textAlign: 'right' }}>Number of Donors</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryData
                    .filter(item => item.month_or_total !== 'Yearly Total') // Exclude Yearly Total from the monthly table
                    .map((item, index) => (
                      <tr key={index}>
                        <td>{months[item.month_or_total - 1]}</td>
                        <td style={{ textAlign: 'right' }}>
                          {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.totalDonations || 0)}
                        </td>
                        <td style={{ textAlign: 'right' }}>{item.uniqueMembers || 0}</td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {/* Summary Section */}
              <div className="mt-4">
                <h5>Summary</h5>
                <ul className="list-group list-group-flush">
                  <li className="list-group-item d-flex justify-content-between">
                    <strong>{getCardTitle()} Total: </strong>
                    <span>
                      <strong>
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                          summaryData.find(item => item.month_or_total === 'Yearly Total').totalDonations || 0
                        )}
                      </strong>
                    </span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between">
                    <strong>{getCardTitle()} Total number of donors:</strong>
                    <span>
                      <strong>
                        {summaryData.find(item => item.month_or_total === 'Yearly Total').uniqueDonors || 0}
                      </strong>
                    </span>
                  </li>
                  {/* Removed Average Donation and Highest Donation */}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReceiptDonationsSummaryDashboard;